// Colaboradores (motoristas, agenciadores e outros) e as regras de
// remuneração de cada um, com vigência.
import { appDoMotorista, colaboradores, regras, situacoesDeEnvio, vales, veiculos } from '../dados.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { celular as formatarCelular, cpf as formatarCpf, data as formatarData, dataDoMomento, dinheiro, lerNumero, numeroBR, placa as formatarPlaca } from '../lib/formato.js';
import { linkDoWhatsApp } from '../lib/sos.js';
import { COMPONENTES, resumoRegra, textoVigencia, vigencia } from '../lib/remuneracao.js';
import { apenasDigitos, cpfValido, normalizarCelular } from '../lib/validacao.js';
import { nestaTela } from '../rotas.js';
import { diaSeguinte } from '../lib/datas.js';
import { contaGravavel, hojeDaEmpresa, podeGravar, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import {
  avisar, campo, carregando, cartaoLink, confirmar, dialogo, emblema, faixa, formulario, secao, vazio,
} from '../ui/componentes.js';
import { blocoAcertosDoColaborador } from './acertos.js';
import { faixaDaConta } from './inicio.js';

const FUNCOES = {
  motorista: 'Motorista',
  agenciador: 'Agenciador',
  outro: 'Outro',
};

function nomeExibido(c) {
  return c.apelido ? `${c.nome} (${c.apelido})` : c.nome;
}

function emblemaVigencia(regra, hoje) {
  const estado = vigencia(regra, hoje);
  if (estado === 'valendo') return emblema('Valendo', 'ok');
  if (estado === 'futura') return emblema('Ainda não começou', 'info');
  return emblema('Encerrada');
}

// ---------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------
export function telaColaboradores() {
  const corpo = h('div', { class: 'secao' }, carregando());

  (async () => {
    const [lista, todas, frota, envio] = await Promise.all([colaboradores.listar(), regras.listar(), veiculos.listar(), situacoesDeEnvio('colaboradores')]);
    const hoje = hojeDaEmpresa();
    const gravavel = podeGravar();
    const ativos = lista.filter((c) => c.ativo);
    const inativos = lista.filter((c) => !c.ativo);

    if (lista.length === 0) {
      trocar(corpo, vazio({
        simbolo: 'pessoas',
        titulo: 'Ninguém cadastrado ainda',
        texto: 'Cadastre os motoristas e os agenciadores parceiros. Depois informe a comissão ou o salário de cada um.',
        acao: gravavel && h('a', { class: 'botao', href: '#/colaboradores/novo' }, 'Cadastrar colaborador'),
      }));
      return;
    }

    const cartao = (c) => {
      const ordem = Object.keys(COMPONENTES);
      const valendo = todas
        .filter((r) => r.colaborador_id === c.id && vigencia(r, hoje) === 'valendo')
        .sort((a, b) => ordem.indexOf(a.componente) - ordem.indexOf(b.componente));
      return cartaoLink({
        href: `#/colaboradores/${c.id}`,
        simbolo: 'conta',
        titulo: nomeExibido(c),
        linhas: [
          c.celular && formatarCelular(c.celular),
          valendo.length > 0 ? valendo.map((r) => resumoRegra(r, { veiculos: frota })).join(' · ') : (c.funcao === 'motorista' ? 'Remuneração não informada' : null),
        ],
        emblemas: [emblema(FUNCOES[c.funcao], c.funcao === 'motorista' ? 'info' : ''), !c.ativo && emblema('Desativado'), emblemaEnvio(envio.get(c.id))],
      });
    };

    trocar(corpo, 
      gravavel && h('a', { class: 'botao', href: '#/colaboradores/novo' }, '+ Colaborador'),
      secao('Ativos', h('div', { class: 'lista' }, ativos.map(cartao), ativos.length === 0 && h('p', { class: 'pequeno' }, 'Nenhum ativo.')), { contador: String(ativos.length) }),
      inativos.length > 0 && secao('Desativados', h('div', { class: 'lista' }, inativos.map(cartao))),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: 'Colaboradores', conteudo: [faixaDaConta(), corpo] });
}

// ---------------------------------------------------------------------
// Cadastro e edição
// ---------------------------------------------------------------------
export function telaColaborador({ params }) {
  const tela = nestaTela();
  const novo = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, novo ? 'Novo colaborador' : 'Colaborador');

  (async () => {
    const c = novo ? null : await colaboradores.obter(params.id);
    if (!novo && !c) {
      trocar(corpo, vazio({ simbolo: 'pessoas', titulo: 'Colaborador não encontrado', acao: h('a', { class: 'botao', href: '#/colaboradores' }, 'Ver colaboradores') }));
      return;
    }
    if (c) titulo.textContent = c.apelido || c.nome;
    const gravavel = podeGravar();

    const form = formulario({
      filhos: [
        campo({ rotulo: 'Nome completo', nome: 'nome', obrigatorio: true, valor: c?.nome ?? '', formato: 'nome', atributos: { autocomplete: 'off', autocapitalize: 'words' } }),
        campo({ rotulo: 'Apelido', nome: 'apelido', valor: c?.apelido ?? '', formato: 'nome', dica: 'Como é chamado no dia a dia.' }),
        campo({
          rotulo: 'Função',
          nome: 'funcao',
          tipo: 'segmentos',
          obrigatorio: true,
          valor: c?.funcao ?? 'motorista',
          opcoes: [['motorista', 'Motorista'], ['agenciador', 'Agenciador', 'parceiro'], ['outro', 'Outro']],
        }),
        campo({ rotulo: 'CPF', nome: 'cpf', valor: c?.cpf ? formatarCpf(c.cpf) : '', formato: 'cpf', atributos: { inputmode: 'numeric', autocomplete: 'off' } }),
        campo({
          rotulo: 'Celular com DDD',
          nome: 'celular',
          tipo: 'tel',
          valor: c?.celular ? formatarCelular(c.celular) : '',
          dica: 'Mais adiante, o motorista vai poder mandar fotos e áudios pelo WhatsApp deste número.',
          atributos: { inputmode: 'tel', autocomplete: 'off', placeholder: '(48) 99999-0000' },
        }),
        campo({ rotulo: 'Chave Pix', nome: 'chave_pix', valor: c?.chave_pix ?? '', atributos: { autocomplete: 'off', autocapitalize: 'none' } }),
        // Modo viagem (0.23.0): os vencimentos do motorista entram nos avisos da viagem.
        h('div', { class: 'grade-2', dataset: { soMotorista: '1' } },
          campo({ rotulo: 'CNH vale até', nome: 'cnh_validade', tipo: 'date', valor: c?.cnh_validade ?? '' }),
          campo({ rotulo: 'Exame toxicológico vale até', nome: 'toxicologico_ate', tipo: 'date', valor: c?.toxicologico_ate ?? '' })),
        campo({ rotulo: 'Observação', nome: 'observacao', tipo: 'textarea', valor: c?.observacao ?? '' }),
      ],
      textoBotao: novo ? 'Cadastrar' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (d) => {
        const erros = {};
        if (d.nome.length < 2) erros.nome = 'Informe o nome.';
        if (!d.funcao) erros.funcao = 'Escolha a função.';
        if (d.cpf && !cpfValido(d.cpf)) erros.cpf = 'CPF inválido. Confira os números.';
        if (d.celular && !normalizarCelular(d.celular)) erros.celular = 'Informe DDD + número, como (48) 99999-0000.';
        return erros;
      },
      aoEnviar: async (d) => {
        const dados = {
          nome: d.nome,
          apelido: d.apelido || null,
          funcao: d.funcao,
          cpf: d.cpf ? apenasDigitos(d.cpf) : null,
          celular: d.celular ? normalizarCelular(d.celular) : null,
          chave_pix: d.chave_pix || null,
          cnh_validade: d.funcao === 'motorista' ? d.cnh_validade || null : null,
          toxicologico_ate: d.funcao === 'motorista' ? d.toxicologico_ate || null : null,
          observacao: d.observacao || null,
        };
        if (novo) {
          const criado = await colaboradores.criar(dados);
          avisar(dados.funcao === 'motorista' ? 'Motorista cadastrado. Agora informe como ele recebe.' : 'Colaborador cadastrado.');
          tela.ir(`/colaboradores/${criado.id}`, { substituir: true });
        } else {
          await colaboradores.atualizar(c.id, dados, c);
          avisar('Alterações salvas.');
          tela.ir('/colaboradores');
        }
      },
    });
    // As validades da CNH e do exame toxicológico, só para o motorista.
    const blocoDoMotorista = form.querySelector('[data-so-motorista]');
    const mostrarDoMotorista = () => {
      blocoDoMotorista.hidden = (form.querySelector('[name="funcao"]:checked')?.value ?? 'motorista') !== 'motorista';
    };
    for (const r of form.querySelectorAll('[name="funcao"]')) r.addEventListener('change', mostrarDoMotorista);
    mostrarDoMotorista();

    trocar(corpo, 
      c && !c.ativo && faixa('Este colaborador está desativado.', 'alerta'),
      c && blocoAppDoMotorista(c, tela),
      c && await blocoAcertosDoColaborador(c, gravavel),
      c && await blocoRegras(c, gravavel),
      c && await blocoVales(c, gravavel),
      secao(novo ? 'Dados' : 'Dados pessoais', form),
      c && gravavel && blocoDesativar(c),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo, voltar: '/colaboradores', conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
}

// App do motorista e do agenciador (0.21.0): o link pessoal pelo WhatsApp
// (só com internet). Depois de mandar ou tirar o acesso, a tela inteira é
// refeita (ela pode ter sido redesenhada enquanto o servidor respondia, com a
// situação de antes).
const APP_DA_PESSOA = {
  motorista: {
    titulo: 'App do motorista',
    caminho: 'motorista',
    explica: 'No app, o motorista vê as viagens, as cargas com a comissão, os vales e o saldo dele; completa os dados da carga (CT-e, peso…), lança as despesas da viagem com foto e usa o SOS mecânica. Ele entra pelo link que você manda pelo WhatsApp, sem e-mail e sem senha. O link não vence: vale uma vez.',
    tirar: 'O celular dele deixa de ver as viagens e de lançar despesas. Para voltar, mande um link novo.',
    mensagem: 'as suas viagens, cargas e vales, o saldo, as despesas da viagem com foto e o SOS mecânica',
  },
  agenciador: {
    titulo: 'App do agenciador',
    caminho: 'agenciador',
    explica: 'No app, o agenciador vê só as cargas que ele agenciou (em andamento e as entregues), o agenciamento de cada uma, os acertos e o que os clientes dessas cargas ainda devem, os caminhões vazios (onde e desde quando), o Raio-X do frete e o Radar de retorno, com os números da empresa. Ele não lança nada. Entra pelo link que você manda pelo WhatsApp, sem e-mail e sem senha. O link não vence: vale uma vez.',
    tirar: 'O celular dele deixa de ver as cargas e os caminhões vazios. Para voltar, mande um link novo.',
    mensagem: 'as cargas que você agenciou, o que tem a receber, os caminhões vazios, o Raio-X do frete e o Radar de retorno',
  },
};

function blocoAppDoMotorista(c, tela) {
  const textos = APP_DA_PESSOA[c.funcao];
  if (!textos) return null;
  const cuida = sessao.papel === 'titular' || sessao.papel === 'escritorio';
  const lugar = h('div', { class: 'lista' }, carregando());

  const desenhar = async () => {
    const s = await appDoMotorista.situacao(c.id);
    trocar(lugar,
      s === null
        ? h('p', { class: 'pequeno' }, 'Sem internet agora: a situação do app aparece quando o sinal voltar.')
        : s.usando_desde
          ? h('p', null, `${c.apelido || c.nome.split(' ')[0]} usa o app no celular desde ${dataDoMomento(s.usando_desde)}.`)
          : s.link_mandado_em
            ? h('p', null, `Link mandado em ${dataDoMomento(s.link_mandado_em)}. Ele ainda não abriu.`)
            : h('p', { class: 'pequeno' }, textos.explica),
      cuida && c.ativo && s !== null && h('button', { class: 'botao secundario', type: 'button', onClick: (e) => mandarLink(e.currentTarget) },
        s.usando_desde ? 'Mandar um link novo (trocou de celular)' : s.link_mandado_em ? 'Mandar o link de novo' : 'Mandar o link do app'),
      cuida && s && (s.usando_desde || s.link_mandado_em) && h('button', { class: 'botao texto', type: 'button', onClick: () => tirar() }, 'Tirar o acesso do app'));
  };

  async function mandarLink(botao) {
    botao.disabled = true;
    try {
      const { codigo } = await appDoMotorista.criarLink(c.id);
      await mensagemDoLink({ c, textos, endereco: `${location.origin}${location.pathname}#/${textos.caminho}/${codigo}` });
      tela.renderizar();
    } catch (erro) {
      botao.disabled = false;
      avisar(mensagemDeErro(erro), 'erro');
    }
  }

  async function tirar() {
    const ok = await confirmar({
      titulo: 'Tirar o acesso do app?',
      texto: textos.tirar,
      sim: 'Tirar o acesso',
      perigo: true,
    });
    if (!ok) return;
    try {
      await appDoMotorista.tirar(c.id);
      avisar('Acesso do app retirado.');
      tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  }

  desenhar().catch((erro) => trocar(lugar, faixa(mensagemDeErro(erro), 'perigo')));
  return secao(textos.titulo, lugar);
}

function mensagemDoLink({ c, textos, endereco }) {
  const empresa = sessao.empresa.nome_fantasia || sessao.empresa.nome;
  const nome = c.apelido || c.nome.split(' ')[0];
  const texto = [
    `Olá, ${nome}! Este é o seu acesso ao app do HSA Frota da ${empresa}: ${textos.mensagem}.`,
    endereco,
    'Toque no link no seu celular. Ele entra uma vez só, no seu celular: não passe para ninguém.',
  ].join('\n\n');
  return dialogo((fechar) => [
    h('h2', null, 'Link pronto'),
    h('p', null, 'Mande a mensagem abaixo para ', h('strong', null, nome), '. Ele abre no celular dele e já fica entrado.'),
    h('pre', { class: 'mensagem-pronta' }, texto),
    h('div', { class: 'acoes' },
      h('a', {
        class: 'botao cheio',
        href: c.celular ? linkDoWhatsApp(c.celular, texto) : `https://wa.me/?text=${encodeURIComponent(texto)}`,
        target: '_blank',
        rel: 'noopener',
        onClick: () => setTimeout(() => fechar(), 300),
      }, 'Mandar pelo WhatsApp'),
      h('button', {
        class: 'botao cheio secundario',
        type: 'button',
        onClick: async () => {
          try {
            await navigator.clipboard.writeText(texto);
            avisar('Mensagem copiada. Cole na conversa com ele.');
          } catch {
            avisar('Não deu para copiar aqui. Selecione o texto e copie.', 'erro');
          }
        },
      }, 'Copiar a mensagem'),
      h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar() }, 'Fechar')),
  ]);
}

async function blocoRegras(c, gravavel) {
  const [lista, frota] = await Promise.all([regras.doColaborador(c.id), veiculos.listar()]);
  const hoje = hojeDaEmpresa();
  const ordem = { valendo: 0, futura: 1, encerrada: 2 };
  lista.sort((a, b) => ordem[vigencia(a, hoje)] - ordem[vigencia(b, hoje)] || (a.vigente_de < b.vigente_de ? 1 : -1));

  return secao('Remuneração', h('div', { class: 'lista' },
    lista.length === 0 && h('p', { class: 'pequeno' },
      c.funcao === 'motorista'
        ? 'Informe a comissão, o salário ou o que mais ele recebe. O acerto usa essas regras para calcular.'
        : 'Nenhuma regra cadastrada.'),
    lista.map((r) => cartaoLink({
      href: `#/regras/${r.id}`,
      titulo: resumoRegra(r, { veiculos: frota }),
      linhas: [textoVigencia(r)],
      emblemas: [emblemaVigencia(r, hoje)],
    })),
    gravavel && c.ativo && h('a', { class: 'botao secundario', href: `#/colaboradores/${c.id}/regras/nova` }, '+ Adicionar remuneração')));
}

async function blocoVales(c, gravavel) {
  const [lista, envio] = await Promise.all([vales.doColaborador(c.id), situacoesDeEnvio('lancamentos')]);
  if (lista.length === 0 && !gravavel) return null;
  const recentes = lista.slice(0, 10);
  return secao('Vales', h('div', { class: 'lista' },
    lista.length === 0 && h('p', { class: 'pequeno' }, 'Nenhum vale lançado.'),
    recentes.map((l) => cartaoLink({
      href: `#/vales/${l.id}`,
      simbolo: 'dinheiro',
      titulo: `${dinheiro(l.valor)} · ${formatarData(l.data)}`,
      linhas: [l.descricao],
      emblemas: [l.assinatura ? emblema('Assinado', 'ok') : emblema('Sem assinatura'), emblemaEnvio(envio.get(l.id))],
    })),
    lista.length > recentes.length && h('p', { class: 'pequeno' }, `Mostrando os ${recentes.length} mais recentes de ${lista.length}.`),
    gravavel && c.ativo && h('a', { class: 'botao secundario', href: `#/vales/novo?colaborador=${c.id}` }, '+ Vale')), { contador: lista.length ? String(lista.length) : null });
}

function blocoDesativar(c) {
  const tela = nestaTela();
  const alternar = async () => {
    const ok = await confirmar({
      titulo: c.ativo ? `Desativar ${c.apelido || c.nome}?` : `Reativar ${c.apelido || c.nome}?`,
      texto: c.ativo ? 'Ele sai das listas de lançamento. O histórico e os acertos continuam guardados.' : 'Ele volta para as listas de lançamento.',
      sim: c.ativo ? 'Desativar' : 'Reativar',
    });
    if (!ok) return;
    try {
      await colaboradores.atualizar(c.id, { ativo: !c.ativo });
      avisar(c.ativo ? 'Colaborador desativado.' : 'Colaborador reativado.');
      tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };
  const excluir = async () => {
    const ok = await confirmar({
      titulo: 'Excluir este cadastro?',
      texto: 'Use só para cadastros feitos por engano. O registro vai para a lixeira.',
      sim: 'Excluir',
      perigo: true,
    });
    if (!ok) return;
    try {
      await colaboradores.excluir(c.id);
      avisar('Cadastro excluído.');
      tela.ir('/colaboradores', { substituir: true });
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };
  return h('div', { class: 'acoes' },
    h('button', { class: 'botao cheio secundario', type: 'button', onClick: alternar }, c.ativo ? 'Desativar colaborador' : 'Reativar colaborador'),
    h('button', { class: 'botao texto', type: 'button', onClick: excluir }, 'Excluir cadastro feito por engano'));
}

// ---------------------------------------------------------------------
// Regra de remuneração
// ---------------------------------------------------------------------
export function telaRegra({ params }) {
  const tela = nestaTela();
  const nova = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, nova ? 'Nova remuneração' : 'Remuneração');
  let voltarPara = nova ? `/colaboradores/${params.colaborador}` : '/colaboradores';
  const pagina = casca({ titulo, voltar: voltarPara, conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
  const linkVoltar = pagina.querySelector('.topo a[aria-label="Voltar"]');

  (async () => {
    const r = nova ? null : await regras.obter(params.id);
    if (!nova && !r) {
      trocar(corpo, vazio({ simbolo: 'pessoas', titulo: 'Regra não encontrada', acao: h('a', { class: 'botao', href: '#/colaboradores' }, 'Ver colaboradores') }));
      return;
    }
    const colaboradorId = r?.colaborador_id ?? params.colaborador;
    voltarPara = `/colaboradores/${colaboradorId}`;
    linkVoltar?.setAttribute('href', `#${voltarPara}`);
    const [c, frota, existentes] = await Promise.all([colaboradores.obter(colaboradorId), veiculos.listar(), regras.doColaborador(colaboradorId)]);
    if (!c) {
      trocar(corpo, vazio({ simbolo: 'pessoas', titulo: 'Colaborador não encontrado', acao: h('a', { class: 'botao', href: '#/colaboradores' }, 'Ver colaboradores') }));
      return;
    }
    titulo.textContent = `Remuneração · ${c.apelido || c.nome}`;
    const gravavel = podeGravar();
    const hoje = hojeDaEmpresa();
    const tratores = frota.filter((v) => v.tipo === 'trator' && (v.ativo || v.id === r?.veiculo_id));
    const opcoesVeiculo = [['', 'Todos os caminhões'], ...tratores.map((t) => [t.id, `Só no ${formatarPlaca(t.placa)}${t.apelido ? ` · ${t.apelido}` : ''}`])];
    // Caminhão excluído depois: mantém o vínculo em vez de virar "todos"
    if (r?.veiculo_id && !tratores.some((t) => t.id === r.veiculo_id)) opcoesVeiculo.push([r.veiculo_id, 'Caminhão excluído do cadastro']);

    // Nova regra começa depois do fim da anterior do mesmo tipo, se ela estiver terminando.
    const sugerirInicio = (componente, veiculoId) => {
      const fins = existentes
        .filter((x) => x.componente === componente && (x.veiculo_id ?? '') === (veiculoId ?? '') && x.vigente_ate && x.vigente_ate >= hoje)
        .map((x) => x.vigente_ate)
        .sort();
      return fins.length ? diaSeguinte(fins[fins.length - 1]) : hoje;
    };
    let dataMexida = !nova;
    const sugerido = c.funcao === 'agenciador' ? 'agenciamento' : 'comissao_frete';
    const modoEstadiaInicial = r?.componente === 'repasse_estadia' && r.valor !== null ? 'valor' : 'percentual';

    const numero = (valor) => (valor === null || valor === undefined ? '' : String(valor).replace('.', ','));

    const blocos = {
      percentual: campo({ rotulo: 'Percentual (%)', nome: 'percentual', valor: numero(r?.percentual), formato: 'percentual', atributos: { inputmode: 'decimal', placeholder: 'Ex.: 13' } }),
      valor: campo({ rotulo: 'Valor (R$)', nome: 'valor', valor: r?.valor != null ? numeroBR(r.valor, { casas: 2 }) : '', formato: 'dinheiro', atributos: { inputmode: 'decimal', placeholder: 'Ex.: 150,00' } }),
      base: campo({
        rotulo: 'Sobre qual valor do frete?',
        nome: 'base',
        tipo: 'segmentos',
        valor: r?.base ?? 'bruto',
        opcoes: [['bruto', 'Frete bruto', 'valor cheio da carga'], ['liquido', 'Frete líquido', 'depois de pedágio e impostos']],
      }),
      inclui_estadia: campo({ rotulo: 'A comissão também vale sobre a estadia cobrada', nome: 'inclui_estadia', tipo: 'caixa', valor: r?.inclui_estadia ?? false }),
      modo_estadia: campo({
        rotulo: 'Como é o repasse?',
        nome: 'modo_estadia',
        tipo: 'segmentos',
        valor: modoEstadiaInicial,
        opcoes: [['percentual', 'Percentual', '% da estadia'], ['valor', 'Valor fixo', 'R$ por estadia']],
        aoMudar: () => atualizarVisiveis(),
      }),
      fixo_pagamento: campo({
        rotulo: 'Como é pago?',
        nome: 'fixo_pagamento',
        tipo: 'segmentos',
        valor: r?.fixo_pagamento ?? 'acerto',
        opcoes: [['acerto', 'No acerto', 'entra no saldo da viagem'], ['folha', 'Na folha', 'pago à parte']],
      }),
      com_faixa: campo({
        rotulo: 'Valor maior quando o frete passa de um limite',
        nome: 'com_faixa',
        tipo: 'caixa',
        valor: r?.faixa_ate != null,
        dica: 'Ex.: R$ 150,00 para frete até R$ 10.000,00 e R$ 200,00 acima disso.',
        aoMudar: () => atualizarVisiveis(),
      }),
      faixa: h('div', { class: 'secao' },
        h('div', { class: 'grade-2' },
          campo({ rotulo: 'Frete acima de (R$)', nome: 'faixa_ate', formato: 'dinheiro', valor: r?.faixa_ate != null ? numeroBR(r.faixa_ate, { casas: 2 }) : '', atributos: { inputmode: 'decimal', placeholder: '10.000,00' } }),
          campo({ rotulo: 'Paga (R$)', nome: 'valor_acima', formato: 'dinheiro', valor: r?.valor_acima != null ? numeroBR(r.valor_acima, { casas: 2 }) : '', atributos: { inputmode: 'decimal', placeholder: '200,00' } })),
        campo({
          rotulo: 'O limite olha qual valor do frete?',
          nome: 'base_faixa',
          tipo: 'segmentos',
          valor: r?.base ?? 'bruto',
          opcoes: [['bruto', 'Frete bruto', 'valor cheio da carga'], ['liquido', 'Frete líquido', 'depois de pedágio e impostos']],
        })),
    };

    const seletorComponente = campo({
      rotulo: 'Tipo de remuneração',
      nome: 'componente',
      tipo: 'select',
      obrigatorio: true,
      valor: r?.componente ?? sugerido,
      desativado: !nova,
      opcoes: Object.entries(COMPONENTES).map(([k, info]) => [k, info.nome]),
      aoMudar: () => atualizarVisiveis(),
    });
    const ajuda = h('p', { class: 'dica' });
    seletorComponente.append(ajuda);

    const form = formulario({
      filhos: [
        seletorComponente,
        blocos.modo_estadia,
        blocos.percentual,
        blocos.valor,
        blocos.base,
        blocos.inclui_estadia,
        blocos.fixo_pagamento,
        blocos.com_faixa,
        blocos.faixa,
        campo({
          rotulo: 'Vale para qual caminhão?',
          nome: 'veiculo_id',
          tipo: 'select',
          valor: r?.veiculo_id ?? '',
          opcoes: opcoesVeiculo,
          dica: 'Use quando o percentual muda conforme o caminhão.',
          aoMudar: () => sugerirData(),
        }),
        h('div', { class: 'grade-2' },
          campo({ rotulo: 'Vale a partir de', nome: 'vigente_de', tipo: 'date', obrigatorio: true, valor: r?.vigente_de ?? sugerirInicio(r?.componente ?? sugerido, ''), aoMudar: () => { dataMexida = true; } }),
          campo({ rotulo: 'Até', nome: 'vigente_ate', tipo: 'date', valor: r?.vigente_ate ?? '', dica: 'Deixe em branco se não tem fim.' })),
        campo({ rotulo: 'Observação', nome: 'observacao', tipo: 'textarea', valor: r?.observacao ?? '' }),
      ],
      textoBotao: nova ? 'Salvar remuneração' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (d) => {
        const erros = {};
        const componente = componenteAtual();
        const usaPercentual = componente === 'comissao_frete' || (componente === 'repasse_estadia' && d.modo_estadia === 'percentual');
        if (usaPercentual) {
          const p = lerNumero(d.percentual);
          if (!(p > 0 && p <= 100)) erros.percentual = 'Informe um percentual entre 0,01 e 100.';
        } else {
          const v = lerNumero(d.valor);
          if (!(v > 0)) erros.valor = 'Informe o valor, como 150,00.';
        }
        if (componente === 'agenciamento' && d.com_faixa) {
          const limite = lerNumero(d.faixa_ate);
          const acima = lerNumero(d.valor_acima);
          if (!(limite > 0)) erros.faixa_ate = 'Informe o limite do frete, como 10.000,00.';
          if (!(acima >= 0) || acima === null) erros.valor_acima = 'Informe o valor acima do limite.';
        }
        if (!d.vigente_de) erros.vigente_de = 'Informe a data de início.';
        if (d.vigente_ate && d.vigente_de && d.vigente_ate < d.vigente_de) erros.vigente_ate = 'A data final precisa ser igual ou depois da inicial.';
        return erros;
      },
      aoEnviar: async (d) => {
        const componente = componenteAtual();
        const usaPercentual = componente === 'comissao_frete' || (componente === 'repasse_estadia' && d.modo_estadia === 'percentual');
        const faixa = componente === 'agenciamento' && Boolean(d.com_faixa);
        const dados = {
          veiculo_id: d.veiculo_id || null,
          percentual: usaPercentual ? lerNumero(d.percentual) : null,
          valor: usaPercentual ? null : lerNumero(d.valor),
          base: componente === 'comissao_frete' ? d.base || 'bruto' : faixa ? d.base_faixa || 'bruto' : null,
          inclui_estadia: componente === 'comissao_frete' ? Boolean(d.inclui_estadia) : false,
          fixo_pagamento: componente === 'fixo_mensal' ? d.fixo_pagamento || 'acerto' : null,
          faixa_ate: faixa ? lerNumero(d.faixa_ate) : null,
          valor_acima: faixa ? lerNumero(d.valor_acima) : null,
          vigente_de: d.vigente_de,
          vigente_ate: d.vigente_ate || null,
          observacao: d.observacao || null,
        };
        if (nova) {
          await regras.criar({ ...dados, colaborador_id: c.id, componente });
          avisar('Remuneração salva.');
        } else {
          await regras.atualizar(r.id, dados, r);
          avisar('Alterações salvas.');
        }
        tela.ir(voltarPara, { substituir: true });
      },
      extras: !nova && gravavel && [
        !r.vigente_ate && vigencia(r, hoje) === 'valendo' && h('button', {
          class: 'botao cheio secundario',
          type: 'button',
          onClick: async () => {
            const ok = await confirmar({
              titulo: 'Encerrar esta regra hoje?',
              texto: 'Ela deixa de valer depois de hoje. Use quando o combinado mudar e depois cadastre a regra nova a partir de amanhã.',
              sim: 'Encerrar hoje',
            });
            if (!ok) return;
            try {
              await regras.atualizar(r.id, { vigente_ate: hoje });
              avisar('Regra encerrada.');
              tela.ir(voltarPara, { substituir: true });
            } catch (erro) {
              avisar(mensagemDeErro(erro), 'erro');
            }
          },
        }, 'Encerrar hoje'),
        h('button', {
          class: 'botao texto',
          type: 'button',
          onClick: async () => {
            const ok = await confirmar({ titulo: 'Excluir esta regra?', texto: 'Use só para regra cadastrada por engano. Para mudanças do combinado, encerre a regra e crie outra.', sim: 'Excluir', perigo: true });
            if (!ok) return;
            try {
              await regras.excluir(r.id);
              avisar('Regra excluída.');
              tela.ir(voltarPara, { substituir: true });
            } catch (erro) {
              avisar(mensagemDeErro(erro), 'erro');
            }
          },
        }, 'Excluir regra feita por engano'),
      ],
    });

    function sugerirData() {
      if (dataMexida) return;
      form.querySelector('[name="vigente_de"]').value = sugerirInicio(componenteAtual(), form.querySelector('[name="veiculo_id"]').value);
    }

    function componenteAtual() {
      return form.querySelector('[name="componente"]').value;
    }

    function atualizarVisiveis() {
      const componente = componenteAtual();
      const modo = form.querySelector('[name="modo_estadia"]:checked')?.value ?? 'percentual';
      const usaPercentual = componente === 'comissao_frete' || (componente === 'repasse_estadia' && modo === 'percentual');
      blocos.modo_estadia.hidden = componente !== 'repasse_estadia';
      blocos.percentual.hidden = !usaPercentual;
      blocos.valor.hidden = usaPercentual;
      blocos.base.hidden = componente !== 'comissao_frete';
      blocos.inclui_estadia.hidden = componente !== 'comissao_frete';
      blocos.fixo_pagamento.hidden = componente !== 'fixo_mensal';
      blocos.com_faixa.hidden = componente !== 'agenciamento';
      blocos.faixa.hidden = componente !== 'agenciamento' || !form.querySelector('[name="com_faixa"]').checked;
      const rotuloValor = {
        valor_por_carga: 'Valor por carga (R$)',
        valor_por_viagem: 'Valor por viagem (R$)',
        diaria: 'Valor da diária (R$)',
        repasse_estadia: 'Valor por estadia (R$)',
        fixo_mensal: 'Valor por mês (R$)',
        agenciamento: 'Valor por carga agenciada (R$)',
      }[componente];
      if (rotuloValor) blocos.valor.querySelector('label').firstChild.textContent = rotuloValor;
      ajuda.textContent = COMPONENTES[componente]?.ajuda ?? '';
      sugerirData();
    }

    atualizarVisiveis();
    trocar(corpo, 
      h('p', { class: 'pequeno' }, 'Cada tipo vale num período. Quando o combinado mudar, encerre a regra atual e crie outra: assim os acertos antigos continuam certos.'),
      form,
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return pagina;
}
